/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authService } from '../../services/auth.service';
import { profileService } from '../../services/profile.service';
import type { LoginRequest, User } from '../../types';
import { handleAsyncThunkError } from '../../utils/error-handler';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  // Resolved permission keys (e.g. "APP.READ") for the logged-in user — the single
  // source the UI reads to show/hide buttons and gate routes.
  permissions: string[];
  permissionsLoaded: boolean;
}

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('accessToken'),
  isAuthenticated: !!localStorage.getItem('accessToken'),
  loading: false,
  error: null,
  permissions: [],
  permissionsLoaded: false,
};

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      const authResponse = await authService.login(credentials);
      const data = authResponse.data;

      // Ambiguous email (same email+mpin in several projects): not logged in yet —
      // return the matched projects so the login screen can show a chooser.
      if (data?.projectSelectionRequired) {
        return { projectSelectionRequired: true as const, projects: data.projects || [] };
      }

      const accessToken = data.accessToken as string;
      const refreshToken = data.refreshToken as string;
      const { personnelId, appId, ministryId } = data;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);

      // Fetch full user data + resolved permissions once, right after login.
      const user = await authService.getCurrentUser();
      const permissions = await profileService.getMyPermissions().catch(() => [] as string[]);
      // Store additional login context
      localStorage.setItem('personnelId', personnelId || '');
      localStorage.setItem('appId', appId || '');
      if (ministryId) localStorage.setItem('ministryId', ministryId);
      return { token: accessToken, user, permissions };
    } catch (error: any) {
      const errorMessage = handleAsyncThunkError(error, 'Login failed. Please try again.');
      return rejectWithValue(errorMessage);
    }
  }
);

// Re-resolve the current user's permissions (on app load with an existing token,
// or after an RBAC change) and refresh the store.
export const fetchPermissions = createAsyncThunk(
  'auth/fetchPermissions',
  async (_, { rejectWithValue }) => {
    try {
      return await profileService.getMyPermissions();
    } catch (error: any) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to fetch permissions.'));
    }
  }
);

export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const user = await authService.getCurrentUser();
      return user;
    } catch (error: any) {
      const errorMessage = handleAsyncThunkError(error, 'Failed to fetch user data.');
      return rejectWithValue(errorMessage);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.permissions = [];
      state.permissionsLoaded = false;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        // Ambiguous email → the login screen shows a project chooser; not signed in yet.
        if ((action.payload as any)?.projectSelectionRequired) return;
        state.isAuthenticated = true;
        state.token = (action.payload as any).token;
        state.user = (action.payload as any).user;
        state.permissions = (action.payload as any).permissions || [];
        state.permissionsLoaded = true;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Current User
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Permissions (bootstrap on load / refresh after RBAC change)
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.permissions = action.payload || [];
        state.permissionsLoaded = true;
      })
      .addCase(fetchPermissions.rejected, (state) => {
        // Keep whatever we had; mark loaded so guards stop spinning.
        state.permissionsLoaded = true;
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;